/** Display helpers for guardian screens (dates from the API are agency-local strings). */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** 'YYYY-MM-DD' (or an ISO timestamp) → 'Mon, Oct 12, 2026'. Unparseable input is returned as is. */
export function formatPortalDate(value: string | null | undefined, withWeekday = true): string {
  if (!value) return '';
  const match = DATE_ONLY.exec(value.slice(0, 10));
  const date = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    ...(withWeekday ? { weekday: 'short' as const } : {}),
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** 'HH:mm' → '8:00 AM'. */
export function formatPortalTime(value: string | null | undefined): string {
  if (!value) return '';
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return value;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** 'Jane, RN' / 'Jane' / 'To be assigned'. */
export function formatClinician(clinician: { firstName: string; credential: string } | null | undefined): string {
  if (!clinician || !clinician.firstName) return 'To be assigned';
  return clinician.credential ? `${clinician.firstName}, ${clinician.credential}` : clinician.firstName;
}

/** Shared focus ring for guardian controls (visible focus, FAM-12). */
export const FOCUS_RING =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2';

/** 'Sam Testpatient' → 'ST'; at most two letters. */
export function initialsOf(name: string | null | undefined): string {
  return (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}
